import type { ReactNode } from 'react';
import { useTranslation } from '~/shared/utils/translation';
import ChevronIcon from '~/icons/chevron-down.svg';
import { Typography } from '~/shared/components';
import type { ReservationStoreBase } from '~/features/product-details/components/ReservationDrawer/types';
import styles from '~/features/product-details/components/ReservationDrawer/ReservationStoreList/ReservationStoreList.module.scss';

type OpeningHour = NonNullable<ReservationStoreBase['c_openingHours']>[number] & {
    isOpen?: boolean;
    willOpenToday?: boolean;
};

export const StoreDetailsSummary = ({
    showAddressTranslation = false,
}: {
    showAddressTranslation?: boolean;
}) => {
    const { translate } = useTranslation();

    return (
        <summary className={styles.storeDetailsSummary}>
            {translate(
                showAddressTranslation ? 'pdp.seeAddressAndOpeningHours' : 'pdp.seeOpeningHours',
            )}
            <ChevronIcon aria-hidden="true" className={styles.storeDetailsIcon} />
        </summary>
    );
};

export const StoreDetailsPanel = ({
    store,
    openingHours,
    children,
}: {
    store: ReservationStoreBase;
    openingHours: OpeningHour[];
    children?: ReactNode;
}) => {
    const { translate } = useTranslation();

    return (
        <div className={styles.storeDetailsPanel}>
            {children}
            {openingHours.slice(0, 7).map((schedule, index) => (
                <Typography
                    variant="paragraph-S"
                    className={styles.openingHoursRow}
                    key={`${store.storeId}-${index}`}
                >
                    <Typography variant="paragraph-S">{schedule.day}</Typography>
                    <Typography variant="paragraph-S">
                        {schedule.opens && schedule.closes
                            ? `${schedule.opens} - ${schedule.closes}`
                            : translate('pdp.closedStore')}
                    </Typography>
                </Typography>
            ))}
        </div>
    );
};

export const StoreDetails = ({
    children,
    onOpen,
    showAddressTranslation,
}: {
    children: ReactNode;
    onOpen?: () => void;
    showAddressTranslation?: boolean;
}) => (
    <details
        className={styles.storeDetails}
        onToggle={(event) => {
            if (event.currentTarget.open) onOpen?.();
        }}
    >
        <StoreDetailsSummary showAddressTranslation={showAddressTranslation} />
        {children}
    </details>
);
